import { inject, Injectable, InjectionToken } from '@angular/core';
import type { LocaleId } from '../../core/i18n/locale-id';
import { importWebChatConfiguratorFromCdn } from './web-chat-configurator-cdn-import';
import type { WebChatConfiguratorConstructor } from './web-chat-configurator.types';

export type {
  WebChatConfiguratorConstructor,
  WebChatConfiguratorInstance,
} from './web-chat-configurator.types';

/**
 * Loads a WebChatConfigurator constructor from a CDN ESM URL.
 */
export type WebChatCdnImporter = (url: string) => Promise<WebChatConfiguratorConstructor>;

/**
 * CDN importer used by {@link CdnWebChatConfiguratorLoader}.
 * Override in unit tests to avoid real dynamic imports.
 */
export const WEB_CHAT_CDN_IMPORTER = new InjectionToken<WebChatCdnImporter>(
  'WEB_CHAT_CDN_IMPORTER',
  {
    providedIn: 'root',
    factory: () => importWebChatConfiguratorFromCdn,
  },
);

/**
 * Loads the remote WebChatConfigurator module (injectable for unit tests).
 */
@Injectable()
export abstract class WebChatConfiguratorLoader {
  /**
   * Resolves the configurator constructor from the CDN URL in environment config.
   * @param url Absolute ESM URL for staging or production Purolator configurator.
   */
  abstract load(url: string): Promise<WebChatConfiguratorConstructor>;
}

declare global {
  interface Window {
    ChatBot?: {
      close?: (...args: unknown[]) => unknown;
      init?: (...args: unknown[]) => unknown;
      open?: (...args: unknown[]) => unknown;
      maximize?: (...args: unknown[]) => unknown;
      __puroCloseHooked?: boolean;
    };
    __PuroWebChatConfigurator?: WebChatConfiguratorConstructor;
    /**
     * App locale override for Omilia `findLocale()` (URL path has no `/fr` on app routes).
     */
    __PuroChatLocale?: LocaleId;
  }
}

/**
 * Production loader: dynamically imports the CDN ESM so CSP `script-src`
 * without `'unsafe-inline'` still allows chatbot bootstrap (CDN hosts allowlisted).
 */
@Injectable({ providedIn: 'root' })
export class CdnWebChatConfiguratorLoader extends WebChatConfiguratorLoader {
  private readonly importFromCdn = inject(WEB_CHAT_CDN_IMPORTER);

  /**
   * Loads (or reuses) `window.__PuroWebChatConfigurator` from the given CDN URL.
   */
  override async load(url: string): Promise<WebChatConfiguratorConstructor> {
    if (window.__PuroWebChatConfigurator) {
      return window.__PuroWebChatConfigurator;
    }

    try {
      const ctor = await this.importFromCdn(url);
      window.__PuroWebChatConfigurator = ctor;
      return ctor;
    } catch (err) {
      if (err instanceof Error && /constructor missing/.test(err.message)) {
        throw err;
      }
      throw new Error(`Failed to load WebChatConfigurator from ${url}`, { cause: err });
    }
  }
}
